import { Injectable, signal } from '@angular/core';

export interface LoadingConfig {
  messageKey?: string;
  indeterminate?: boolean;
  showProgress?: boolean;
  currentLoaded?: number;
  total?: number;
}

export interface ErrorConfig {
  titleKey?: string;
  descriptionKey?: string;
  suggestionsHeadingKey?: string;
  suggestionKeys?: string[];
  refreshButtonKey?: string;
  contactButtonKey?: string;
  reassuranceKey?: string;
}

@Injectable({
  providedIn: 'root'
})
export class LoadingService {
  // Signal-based state management - Loading
  private _isLoading = signal(false);
  private _messageKey = signal('common.loading.preparing_data');
  private _indeterminate = signal(true);
  private _showProgress = signal(true);
  private _currentLoaded = signal(0);
  private _total = signal(100);

  // Signal-based state management - Error
  private _hasError = signal(false);
  private _errorTitleKey = signal('error.loading.title');
  private _errorDescriptionKey = signal('error.loading.description');
  private _errorSuggestionsHeadingKey = signal('error.loading.suggestions_heading');
  private _errorSuggestionKeys = signal<string[]>([
    'error.loading.suggestion.check_connection',
    'error.loading.suggestion.refresh_page',
    'error.loading.suggestion.try_again',
    'error.loading.suggestion.contact_support'
  ]);
  private _errorRefreshButtonKey = signal('error.loading.action.refresh');
  private _errorContactButtonKey = signal('error.loading.action.contact_support');
  private _errorReassuranceKey = signal('error.loading.reassurance');

  // Public read-only signals - Loading
  readonly isLoading = this._isLoading.asReadonly();
  readonly messageKey = this._messageKey.asReadonly();
  readonly indeterminate = this._indeterminate.asReadonly();
  readonly showProgress = this._showProgress.asReadonly();
  readonly currentLoaded = this._currentLoaded.asReadonly();
  readonly total = this._total.asReadonly();

  // Public read-only signals - Error
  readonly hasError = this._hasError.asReadonly();
  readonly errorTitleKey = this._errorTitleKey.asReadonly();
  readonly errorDescriptionKey = this._errorDescriptionKey.asReadonly();
  readonly errorSuggestionsHeadingKey = this._errorSuggestionsHeadingKey.asReadonly();
  readonly errorSuggestionKeys = this._errorSuggestionKeys.asReadonly();
  readonly errorRefreshButtonKey = this._errorRefreshButtonKey.asReadonly();
  readonly errorContactButtonKey = this._errorContactButtonKey.asReadonly();
  readonly errorReassuranceKey = this._errorReassuranceKey.asReadonly();

  /**
   * Show the loading overlay
   * @param config Optional configuration for the loading overlay
   */
  show(config?: LoadingConfig): void {
    if (config) {
      if (config.messageKey !== undefined) {
        this._messageKey.set(config.messageKey);
      }
      if (config.indeterminate !== undefined) {
        this._indeterminate.set(config.indeterminate);
      }
      if (config.showProgress !== undefined) {
        this._showProgress.set(config.showProgress);
      }
      if (config.currentLoaded !== undefined) {
        this._currentLoaded.set(config.currentLoaded);
      }
      if (config.total !== undefined) {
        this._total.set(config.total);
      }
    }
    this._isLoading.set(true);
  }

  /**
   * Hide the loading overlay
   */
  hide(): void {
    this._isLoading.set(false);
    this._hasError.set(false);
    // Reset to defaults
    this._messageKey.set('common.loading.preparing_data');
    this._indeterminate.set(true);
    this._showProgress.set(true);
    this._currentLoaded.set(0);
    this._total.set(100);
  }

  /**
   * Show loading with progress tracking
   * @param currentLoaded Current loaded value
   * @param total Total value
   * @param messageKey Optional message key
   */
  showWithProgress(currentLoaded: number, total: number, messageKey?: string): void {
    this.show({
      indeterminate: false,
      showProgress: true,
      currentLoaded,
      total,
      messageKey: messageKey || 'common.loading.preparing_data'
    });
  }

  /**
   * Update progress values
   * @param currentLoaded Current loaded value
   * @param total Optional total value
   */
  updateProgress(currentLoaded: number, total?: number): void {
    this._currentLoaded.set(currentLoaded);
    if (total !== undefined) {
      this._total.set(total);
    }
  }

  /**
   * Show the error overlay
   * @param config Optional configuration for the error overlay
   */
  showError(config?: ErrorConfig): void {
    // First hide any loading state
    this._isLoading.set(false);

    // Apply error configuration if provided
    if (config) {
      if (config.titleKey !== undefined) {
        this._errorTitleKey.set(config.titleKey);
      }
      if (config.descriptionKey !== undefined) {
        this._errorDescriptionKey.set(config.descriptionKey);
      }
      if (config.suggestionsHeadingKey !== undefined) {
        this._errorSuggestionsHeadingKey.set(config.suggestionsHeadingKey);
      }
      if (config.suggestionKeys !== undefined) {
        this._errorSuggestionKeys.set(config.suggestionKeys);
      }
      if (config.refreshButtonKey !== undefined) {
        this._errorRefreshButtonKey.set(config.refreshButtonKey);
      }
      if (config.contactButtonKey !== undefined) {
        this._errorContactButtonKey.set(config.contactButtonKey);
      }
      if (config.reassuranceKey !== undefined) {
        this._errorReassuranceKey.set(config.reassuranceKey);
      }
    }

    // Show the error
    this._hasError.set(true);
  }

  /**
   * Hide the error overlay
   */
  hideError(): void {
    this._hasError.set(false);
    // Reset to defaults
    this._errorTitleKey.set('error.loading.title');
    this._errorDescriptionKey.set('error.loading.description');
    this._errorSuggestionsHeadingKey.set('error.loading.suggestions_heading');
    this._errorSuggestionKeys.set([
      'error.loading.suggestion.check_connection',
      'error.loading.suggestion.refresh_page',
      'error.loading.suggestion.try_again',
      'error.loading.suggestion.contact_support'
    ]);
    this._errorRefreshButtonKey.set('error.loading.action.refresh');
    this._errorContactButtonKey.set('error.loading.action.contact_support');
    this._errorReassuranceKey.set('error.loading.reassurance');
  }
}
